import type {
  BuyerSubmissionListParams,
  BuyerSubmissionStatus,
  BuyerSubmissionSummaryDto,
} from "@/api/buyers";
import type { ListFilterDef } from "@/hooks/useListFilters";
import {
  SUBMISSION_FILTERS,
  type ListViewPreset,
  type SubmissionCardFilter,
} from "./buyerFilters";
import { submissionPhaseLadder, SUBMISSION_TERMINAL_STATUSES } from "./submissionPhases";

// The Submissions list's saved views (D5), its needs-action reasons (D11) and
// the descriptor the page hands to `useListFilters`. Beside `buyerFilters.ts`
// rather than inside it: that file holds the vocabularies the four lists
// SHARE, and a view is one page's own.

/** Mirrors the server's `BuyerCrmTiles` windows, which compute both the
 *  attention counts and their drill-throughs. */
export const BROKER_RESPONSE_DAYS = 7;
export const QUIET_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

const TERMINAL = new Set<BuyerSubmissionStatus>(SUBMISSION_TERMINAL_STATUSES);

/** Why a submission row needs the specialist's attention, in urgency order. */
export type SubmissionNeedsActionReason =
  | "FollowUpOverdue"
  | "AwaitingBroker"
  | "PackageNotSent"
  | "NdaOutstanding"
  | "NoActivity";

/**
 * The reasons a row needs action, COMPUTED CLIENT-SIDE from the summary row.
 *
 * <p>🔴 The server has no needs-action for submissions and the summary row does
 * not carry the milestone stamps its four attention predicates read
 * (`PackageSentAtUtc`, `BrokerRespondedAtUtc`, `BuyerApprovedAtUtc`,
 * `NdaSignedAtUtc`), so each reason here is the closest reading of the fields
 * the row DOES carry — `status`, `ndaOnFile`, `isFollowUpOverdue`,
 * `lastConversationAtUtc`, `createdAt`. They are badges, not the filter: the
 * five chips beside the "Needs action" view each ride the server's own
 * expression, so a reader who wants the exact set clicks the chip.</p>
 *
 * <ul>
 *   <li>Overdue follow-up — the server's own flag, verbatim.</li>
 *   <li>Awaiting broker — outreach is out and nobody has answered for
 *       {@link BROKER_RESPONSE_DAYS}, measured from the newest conversation or,
 *       with none, from when the submission was presented.</li>
 *   <li>Package not sent — the package is drafted and not yet out.</li>
 *   <li>NDA outstanding — past the buyer's go-ahead with the NDA question
 *       unsettled either way.</li>
 *   <li>No activity — live, older than {@link QUIET_DAYS}, and no conversation
 *       logged inside that window.</li>
 * </ul>
 *
 * <p>Every reason but the overdue flag applies to LIVE submissions only — a
 * conversation that ended needs nothing from anybody.</p>
 */
export function submissionNeedsActionReasons(
  s: BuyerSubmissionSummaryDto,
  now: number = Date.now(),
): SubmissionNeedsActionReason[] {
  const reasons: SubmissionNeedsActionReason[] = [];
  if (s.isFollowUpOverdue) reasons.push("FollowUpOverdue");
  if (TERMINAL.has(s.status)) return reasons;

  const lastTouch = Date.parse(s.lastConversationAtUtc ?? s.createdAt);
  const quietSince = Number.isNaN(lastTouch) ? null : (now - lastTouch) / DAY_MS;

  if (s.status === "OutreachSent" && quietSince !== null && quietSince >= BROKER_RESPONSE_DAYS)
    reasons.push("AwaitingBroker");
  if (s.status === "PackageGenerated") reasons.push("PackageNotSent");
  if (!s.ndaOnFile && s.status !== "Shortlisted") reasons.push("NdaOutstanding");
  const age = (now - Date.parse(s.createdAt)) / DAY_MS;
  if (age >= QUIET_DAYS && quietSince !== null && quietSince >= QUIET_DAYS)
    reasons.push("NoActivity");
  return reasons;
}

/**
 * The five reason chips of the "Needs action" view, each a SERVER
 * drill-through. `card` is the four attention pills the page already carried
 * (`SubmissionCardFilter`); `overdue` is the existing follow-up flag, and it
 * rides its own URL param rather than the card's.
 */
export const SUBMISSION_ACTION_REASONS: {
  key: Exclude<SubmissionCardFilter, ""> | "overdue";
  label: string;
  reason: SubmissionNeedsActionReason;
  params: Partial<BuyerSubmissionListParams>;
}[] = [
  {
    key: "overdue",
    label: "Overdue follow-up",
    reason: "FollowUpOverdue",
    params: { overdueFollowUpOnly: true },
  },
  {
    key: "awaiting-broker",
    label: `Awaiting broker ${BROKER_RESPONSE_DAYS}+ days`,
    reason: "AwaitingBroker",
    params: { awaitingBrokerResponseDays: BROKER_RESPONSE_DAYS },
  },
  {
    key: "package-not-sent",
    label: "Package generated, not sent",
    reason: "PackageNotSent",
    params: { packageGeneratedNotSentOnly: true },
  },
  {
    key: "nda-outstanding",
    label: "NDA outstanding",
    reason: "NdaOutstanding",
    params: { ndaOutstandingOnly: true },
  },
  {
    key: "quiet",
    label: `No activity in ${QUIET_DAYS} days`,
    reason: "NoActivity",
    params: { noActivityWithinDays: QUIET_DAYS },
  },
];

/**
 * The saved views.
 *
 * <p>`needs-action` sends `liveOnly` (every reason applies to a live
 * submission) and the PAGE narrows the rows it gets to those carrying a reason,
 * so the view is a union computed on the page in hand rather than a server
 * set. `closed-out` enumerates the terminal five from the one list the phase
 * ladder owns, so a sixth terminal status is a one-line change there.</p>
 */
export const SUBMISSION_VIEWS: ListViewPreset<BuyerSubmissionListParams>[] = [
  {
    key: "needs-action",
    label: "Needs action",
    params: { liveOnly: true },
    sort: "follow-up",
    description: "Live submissions with something waiting on you.",
  },
  { key: "live", label: "Live", params: { liveOnly: true } },
  { key: "under-contract", label: "Under contract", params: { status: ["UnderContract"] } },
  { key: "closed-out", label: "Closed out", params: { status: [...SUBMISSION_TERMINAL_STATUSES] } },
  { key: "all", label: "All submissions", params: {} },
];

const SUBMISSION_VIEW_TOKENS = new Set(SUBMISSION_VIEWS.map((v) => v.key));

/** The view the reader lands on with no choice made anywhere: their own work
 *  to do (F2), everyone else's the whole list. */
export function defaultSubmissionView(isSpecialist: boolean): string {
  return isSpecialist ? "needs-action" : "all";
}

/**
 * The twenty statuses grouped by stage for the More-filters panel — the SAME
 * six rungs the detail header's stepper renders, plus "Lost" for the five off
 * the ladder. Read from `submissionPhaseLadder` rather than retyped, so the
 * panel and the stepper cannot tell different stories.
 */
export const SUBMISSION_STATUS_GROUPS: { key: string; label: string; statuses: readonly BuyerSubmissionStatus[] }[] = [
  ...submissionPhaseLadder.phases.map((p) => ({ key: p.key, label: p.label, statuses: p.statuses })),
  { key: "lost", label: "Lost", statuses: SUBMISSION_TERMINAL_STATUSES },
];

const viewFilter: ListFilterDef<string> = {
  control: "hidden",
  defaultValue: "",
  parse: (raw) => (SUBMISSION_VIEW_TOKENS.has(raw) ? raw : undefined),
};

/** D4 "Show archived" — in the link like every other filter here, so a copied
 *  link keeps it on. Its control is the page's checkbox, not a dropdown. */
const archivedFilter: ListFilterDef<boolean> = {
  control: "hidden",
  defaultValue: false,
  parse: (raw) => (raw === "1" ? true : raw === "" ? false : undefined),
  serialize: (v) => (v ? "1" : null),
};

/** The shared descriptor plus this page's view and archived params. Every URL
 *  the shared descriptor accepted is still accepted, byte for byte. */
export const SUBMISSION_LIST_FILTERS = {
  ...SUBMISSION_FILTERS,
  view: viewFilter,
  archived: archivedFilter,
};
